import { Inject, Injectable } from '@angular/core'
import {
  API_CONFIGURATION,
  ApiClient,
  FullApiClientConfiguration,
  HttpResponseType,
  serializeHttpParams,
  WithResponseHeaders,
} from '@ftr/api-shared'
import { AuditEventItem, AuditLogResponse, UserAuditLoginHistory } from '@ftr/audit-contracts'
import { PageDetails } from '@ftr/contracts/api/core'
import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { Region } from '@ftr/contracts/type/court'
import { Uuid } from '@ftr/contracts/type/shared'
import { AuditApiClientFactory } from '@ftr/data-audit'
import { ApiResult } from '@ftr/foundation'
import { ComplexFilterValue, DateRadioListOptions } from '@ftr/lists'
import { LocalDateTime, ZonedDateTime, ZoneId } from '@js-joda/core'
import { shareReplay, switchMap } from 'rxjs'
import { AuditLogEventDateRange, CourtSystemEntityAuditLogView } from '../types'
import { ExportDownloadAuditLogService } from './export-download-audit-log-service'

export const AUDIT_LOG_PAGE_SIZE = 25
export const AUDIT_LOG_ENDPOINT_PREFIX = 'audit-log'

const GLOBAL_AUDIT_REGION_DEV = Region['ap-southeast-2']
const GLOBAL_AUDIT_REGION_PROD = Region['us-west-2']
const PRODUCTION_WEB_APP_URL = 'https://app.fortherecord.com'

const AUDIT_COURT_SYSTEM_ENTITY_EXPORT_FILENAME = {
  [CourtSystemEntityAuditLogView.AdminUserAuditLog]: 'court-system-user-audit-log.csv',
  [CourtSystemEntityAuditLogView.UserGroupAuditLog]: 'court-system-user-group-audit-log.csv',
  [CourtSystemEntityAuditLogView.BillingGroupAuditLog]: 'court-system-billing-group-audit-log.csv',
  [CourtSystemEntityAuditLogView.CaseAuditLog]: 'court-system-case-audit-log.csv',
  [CourtSystemEntityAuditLogView.HearingAuditLog]: 'court-system-hearing-audit-log.csv',
  [CourtSystemEntityAuditLogView.CourthouseAuditLog]: 'court-system-courthouse-audit-log.csv',
  [CourtSystemEntityAuditLogView.CourtroomAuditLog]: 'court-system-courtroom-audit-log.csv',
  [CourtSystemEntityAuditLogView.DeletionAuditLog]: 'court-system-deletion-audit-log.csv',
  [CourtSystemEntityAuditLogView.DepartmentAuditLog]: 'court-system-department-audit-log.csv',
  [CourtSystemEntityAuditLogView.JudgeAuditLog]: 'court-system-judge-audit-log.csv',
  [CourtSystemEntityAuditLogView.RecordingAuditLog]: 'court-system-recording-audit-log.csv',
  [CourtSystemEntityAuditLogView.RestApiKeyAuditLog]: 'court-system-rest-api-key-audit-log.csv',
}

@Injectable({
  providedIn: 'root',
})
export class AuditLogService {
  private globalApiClient: ApiClient

  constructor(
    private readonly auditApiClientFactory: AuditApiClientFactory,
    private readonly exportDownloadAuditLogService: ExportDownloadAuditLogService,
    @Inject(API_CONFIGURATION) readonly configurationService: FullApiClientConfiguration,
  ) {
    if (configurationService.webAppUrl === PRODUCTION_WEB_APP_URL) {
      this.globalApiClient = auditApiClientFactory.buildForRegion(AUDIT_LOG_ENDPOINT_PREFIX, GLOBAL_AUDIT_REGION_PROD)
    } else {
      this.globalApiClient = auditApiClientFactory.buildForRegion(AUDIT_LOG_ENDPOINT_PREFIX, GLOBAL_AUDIT_REGION_DEV)
    }
  }

  getMyAuditLog(page: number, dateRange?: AuditLogEventDateRange): ApiResult<AuditLogResponse> {
    return this.globalApiClient.get<AuditLogResponse>({
      path: '/user/me',
      params: serializeHttpParams({
        ...new PageDetails(page, AUDIT_LOG_PAGE_SIZE),
        ...convertDateRangeToUTC(dateRange),
      }),
    })
  }

  exportMyAuditLog(dateRange?: AuditLogEventDateRange): ApiResult<WithResponseHeaders<Blob>> {
    const result = this.globalApiClient
      .getWithResponseHeaders<Blob>({
        path: '/user/me/export',
        params: serializeHttpParams({
          ...convertDateRangeToUTC(dateRange),
        }),
        responseType: HttpResponseType.Blob,
      })
      .pipe(shareReplay(1))
    this.exportDownloadAuditLogService.onExport(result, 'my-audit-log.csv')
    return result
  }

  getMyLoginHistory(dateTo?: string): ApiResult<UserAuditLoginHistory> {
    return this.globalApiClient.get<UserAuditLoginHistory>({
      path: '/user/me/last',
      params: serializeHttpParams(
        dateTo
          ? {
              to: dateTo,
            }
          : {},
      ),
    })
  }

  getCourtSystemAuditLog(
    page: number,
    courtSystemId: Uuid,
    dateRange?: AuditLogEventDateRange,
  ): ApiResult<AuditLogResponse> {
    const params = serializeHttpParams({
      ...new PageDetails(page, AUDIT_LOG_PAGE_SIZE),
      ...convertDateRangeToUTC(dateRange),
    })
    return this.auditApiClientFactory.build(AUDIT_LOG_ENDPOINT_PREFIX, courtSystemId).pipe(
      switchMap(apiClient =>
        apiClient.get<AuditLogResponse>({
          path: '/court-system/' + courtSystemId,
          params,
        }),
      ),
    )
  }

  exportCourtSystemAuditLog(
    courtSystemId: Uuid,
    dateRange?: AuditLogEventDateRange,
  ): ApiResult<WithResponseHeaders<Blob>> {
    const result = this.auditApiClientFactory.build(AUDIT_LOG_ENDPOINT_PREFIX, courtSystemId).pipe(
      switchMap(apiClient => {
        return apiClient.getWithResponseHeaders<Blob>({
          path: `/court-system/export/${courtSystemId}`,
          params: serializeHttpParams({
            ...convertDateRangeToUTC(dateRange),
          }),
          responseType: HttpResponseType.Blob,
        })
      }),
      shareReplay(1),
    )
    this.exportDownloadAuditLogService.onExport(result, 'court-system-audit-log.csv')
    return result
  }

  getUserAuditLogEvent(eventId: Uuid): ApiResult<AuditEventItem> {
    return this.globalApiClient.get<AuditEventItem>({
      path: '/user/' + eventId,
    })
  }

  getCourtSystemAuditLogEvent(courtSystemId: Uuid, eventId: Uuid): ApiResult<AuditEventItem> {
    return this.auditApiClientFactory.build(AUDIT_LOG_ENDPOINT_PREFIX, courtSystemId).pipe(
      switchMap(apiClient =>
        apiClient.get<AuditEventItem>({
          path: '/court-system/' + courtSystemId + '/' + eventId,
        }),
      ),
    )
  }

  getCourtSystemEntityAuditLog(
    auditLogView: CourtSystemEntityAuditLogView,
    page: number,
    courtSystemId: Uuid,
    entityId: Uuid,
    dateRange?: AuditLogEventDateRange,
  ): ApiResult<AuditLogResponse> {
    const params = serializeHttpParams({
      ...new PageDetails(page, AUDIT_LOG_PAGE_SIZE),
      ...convertDateRangeToUTC(dateRange),
    })
    return this.auditApiClientFactory.build(AUDIT_LOG_ENDPOINT_PREFIX, courtSystemId).pipe(
      switchMap(apiClient =>
        apiClient.get<AuditLogResponse>({
          path: `/court-system/${getAuditLogPathByView(auditLogView, courtSystemId, entityId)}`,
          params,
        }),
      ),
    )
  }

  getCourtSystemEntityAuditEvent(
    auditLogView: CourtSystemEntityAuditLogView,
    courtSystemId: Uuid,
    eventId: Uuid,
  ): ApiResult<AuditEventItem> {
    return this.auditApiClientFactory.build(AUDIT_LOG_ENDPOINT_PREFIX, courtSystemId).pipe(
      switchMap(apiClient =>
        apiClient.get<AuditEventItem>({
          path: `/court-system/${getAuditEventPathByView(auditLogView, courtSystemId, eventId)}`,
        }),
      ),
    )
  }

  exportCourtSystemEntityAuditLog(
    auditLogView: CourtSystemEntityAuditLogView,
    courtSystemId: Uuid,
    entityId: Uuid,
    dateRange?: AuditLogEventDateRange,
  ): ApiResult<WithResponseHeaders<Blob>> {
    const result = this.auditApiClientFactory.build(AUDIT_LOG_ENDPOINT_PREFIX, courtSystemId).pipe(
      switchMap(apiClient =>
        apiClient.getWithResponseHeaders<Blob>({
          path: `/court-system/${getExportAuditLogPathByView(auditLogView, courtSystemId, entityId)}`,
          params: serializeHttpParams({
            ...convertDateRangeToUTC(dateRange),
          }),
          responseType: HttpResponseType.Blob,
        }),
      ),
      shareReplay(1),
    )

    this.exportDownloadAuditLogService.onExport(
      result,
      AUDIT_COURT_SYSTEM_ENTITY_EXPORT_FILENAME[auditLogView as CourtSystemEntityAuditLogView],
    )
    return result
  }
}

export function getAuditLogEventDateRange(value: ComplexFilterValue | null): AuditLogEventDateRange | undefined {
  const [filterValue, dateFrom, dateTo] = (value ?? []) as string[]
  switch (filterValue) {
    case DateRadioListOptions.SelectDateRange:
      return {
        from: dateFrom,
        to: dateTo,
      }
    default:
      return undefined
  }
}

export function convertDateRangeToUTC(dateRange?: AuditLogEventDateRange): AuditLogEventDateRange | undefined {
  if (!dateRange) {
    return undefined
  }
  return {
    from: convertDateToUTC(`${dateRange.from}T00:00:00.000`),
    to: convertDateToUTC(`${dateRange.to}T23:59:59.999`),
  }
}

function convertDateToUTC(date: string): string {
  return ZonedDateTime.ofLocal(LocalDateTime.parse(date), ZoneId.SYSTEM).withZoneSameInstant(ZoneId.UTC).toString()
}

function getAuditLogPathByView(
  auditLogView: CourtSystemEntityAuditLogView,
  courtSystemId: Uuid,
  entityId: Uuid,
): string {
  switch (auditLogView) {
    case CourtSystemEntityAuditLogView.AdminUserAuditLog:
      return `${courtSystemId}/user/${entityId}`
    case CourtSystemEntityAuditLogView.UserGroupAuditLog:
    case CourtSystemEntityAuditLogView.BillingGroupAuditLog:
      return `${courtSystemId}/user-group/${entityId}`
    case CourtSystemEntityAuditLogView.CaseAuditLog:
      return `${courtSystemId}/case/${entityId}`
    case CourtSystemEntityAuditLogView.HearingAuditLog:
      return `${courtSystemId}/hearing/${entityId}`
    case CourtSystemEntityAuditLogView.CourthouseAuditLog:
      return `${courtSystemId}/courthouse/${entityId}`
    case CourtSystemEntityAuditLogView.CourtroomAuditLog:
      return `${courtSystemId}/courtroom/${entityId}`
    case CourtSystemEntityAuditLogView.JudgeAuditLog:
      return `${courtSystemId}/judge/${entityId}`
    case CourtSystemEntityAuditLogView.DeletionAuditLog:
      return `${courtSystemId}/deletion/${entityId}`
    case CourtSystemEntityAuditLogView.DepartmentAuditLog:
      return `${courtSystemId}/department/${entityId}`
    case CourtSystemEntityAuditLogView.RecordingAuditLog:
      return `${courtSystemId}/recording/${entityId}`
    case CourtSystemEntityAuditLogView.RestApiKeyAuditLog:
      return `${courtSystemId}/rest-api-key/${entityId}`
    default:
      assertUnreachable(auditLogView)
  }
}

function getAuditEventPathByView(
  auditLogView: CourtSystemEntityAuditLogView,
  courtSystemId: Uuid,
  eventId: Uuid,
): string {
  switch (auditLogView) {
    case CourtSystemEntityAuditLogView.UserGroupAuditLog:
    case CourtSystemEntityAuditLogView.BillingGroupAuditLog:
      return `${courtSystemId}/user-group/event/${eventId}`
    case CourtSystemEntityAuditLogView.AdminUserAuditLog:
      return `${courtSystemId}/${eventId}`
    case CourtSystemEntityAuditLogView.CaseAuditLog:
      return `${courtSystemId}/case/event/${eventId}`
    case CourtSystemEntityAuditLogView.HearingAuditLog:
      return `${courtSystemId}/hearing/event/${eventId}`
    case CourtSystemEntityAuditLogView.CourthouseAuditLog:
      return `${courtSystemId}/courthouse/event/${eventId}`
    case CourtSystemEntityAuditLogView.CourtroomAuditLog:
      return `${courtSystemId}/courtroom/event/${eventId}`
    case CourtSystemEntityAuditLogView.JudgeAuditLog:
      return `${courtSystemId}/judge/event/${eventId}`
    case CourtSystemEntityAuditLogView.DeletionAuditLog:
      return `${courtSystemId}/deletion/event/${eventId}`
    case CourtSystemEntityAuditLogView.DepartmentAuditLog:
      return `${courtSystemId}/department/event/${eventId}`
    case CourtSystemEntityAuditLogView.RecordingAuditLog:
      return `${courtSystemId}/recording/event/${eventId}`
    case CourtSystemEntityAuditLogView.RestApiKeyAuditLog:
      return `${courtSystemId}/rest-api-key/event/${eventId}`
    default:
      assertUnreachable(auditLogView)
  }
}

function getExportAuditLogPathByView(
  auditLogView: CourtSystemEntityAuditLogView,
  courtSystemId: Uuid,
  entityId: Uuid,
): string {
  switch (auditLogView) {
    case CourtSystemEntityAuditLogView.AdminUserAuditLog:
      return `export/${courtSystemId}/user/${entityId}`
    case CourtSystemEntityAuditLogView.UserGroupAuditLog:
    case CourtSystemEntityAuditLogView.BillingGroupAuditLog:
      return `${courtSystemId}/user-group/${entityId}/export`
    case CourtSystemEntityAuditLogView.CaseAuditLog:
      return `${courtSystemId}/case/${entityId}/export`
    case CourtSystemEntityAuditLogView.HearingAuditLog:
      return `${courtSystemId}/hearing/${entityId}/export`
    case CourtSystemEntityAuditLogView.CourthouseAuditLog:
      return `${courtSystemId}/courthouse/${entityId}/export`
    case CourtSystemEntityAuditLogView.CourtroomAuditLog:
      return `${courtSystemId}/courtroom/${entityId}/export`
    case CourtSystemEntityAuditLogView.JudgeAuditLog:
      return `${courtSystemId}/judge/${entityId}/export`
    case CourtSystemEntityAuditLogView.DeletionAuditLog:
      return `${courtSystemId}/deletion/${entityId}/export`
    case CourtSystemEntityAuditLogView.DepartmentAuditLog:
      return `${courtSystemId}/department/${entityId}/export`
    case CourtSystemEntityAuditLogView.RecordingAuditLog:
      return `${courtSystemId}/recording/${entityId}/export`
    case CourtSystemEntityAuditLogView.RestApiKeyAuditLog:
      return `${courtSystemId}/rest-api-key/${entityId}/export`
    default:
      assertUnreachable(auditLogView)
  }
}
